import { faTag } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Box, Flex, Text, TextWithIcon } from '@modusign/design-system';
import { LabelWithBookmarkResponse } from '@modusign/openapi-client-category/generated/model';

import CenterLoadingSpinner from '@app/components/CenterLoadingSpinner';
import IconLabel from '@app/features/label/components/IconLabel';
import useAllWorkspaceLabelList from '@app/features/label/hooks/useAllWorkspaceLabelList';
import { LabelFilters } from '@app/features/label/types';

import LabelListItem from '../../components/LabelListItem';

interface LabelButtonListProps extends LabelFilters {
  onNonLabeledSelect?: () => void;
  onSelect: (label: LabelWithBookmarkResponse) => void;
  onClose: () => void;
}

function LabelButtonList({
  onNonLabeledSelect,
  onSelect,
  onClose,
  ...filters
}: LabelButtonListProps) {
  const { data: labels, isLoading } = useAllWorkspaceLabelList(filters);

  if (isLoading) {
    return (
      <Box height="300px" position="relative" width="100%">
        <CenterLoadingSpinner size={36} />
      </Box>
    );
  }

  if (!labels) {
    return null;
  }

  return (
    <Flex as="section" flexDirection="column" maxHeight="300px" overflowY="scroll" py={4}>
      {onNonLabeledSelect && (
        <NonLabelListItemButton
          onClick={() => {
            onNonLabeledSelect();
            onClose();
          }}
        />
      )}
      {labels.map((label) => (
        <LabelListItem key={label.id} label={label}>
          <LabelListItemButton onSelect={() => onSelect(label)}>
            <IconLabel bookmarked={!!label.bookmark} name={label.name} />
          </LabelListItemButton>
        </LabelListItem>
      ))}
      {filters.name && labels.length === 0 && (
        <Text m={4} textAlign="center">
          <Text fontWeight="medium">{`'${filters.name}' `}</Text>
          검색 결과가 없습니다.
        </Text>
      )}
    </Flex>
  );
}

interface LabelListItemButtonProps {
  children: React.ReactNode;
  onSelect: () => void;
}

function LabelListItemButton({ children, onSelect }: LabelListItemButtonProps) {
  return (
    <Box alignSelf="stretch" as="button" flex="auto" height="30px" onClick={onSelect}>
      {children}
    </Box>
  );
}

function NonLabelListItemButton({ onClick }: { onClick: () => void }) {
  return (
    <Flex
      alignItems="center"
      justifyContent="space-between"
      px={4}
      sx={{ '&:hover': { bg: 'tableBackgroundNeutralDark' } }}
    >
      <LabelListItemButton onSelect={onClick}>
        <TextWithIcon
          color="textNeutralLight"
          ellipsis
          leftIcon={
            <Box
              as={FontAwesomeIcon}
              color="iconNeutralSlightlyDark"
              fontSize="14px"
              icon={faTag}
            />
          }
        >
          라벨 없음
        </TextWithIcon>
      </LabelListItemButton>
    </Flex>
  );
}

export default LabelButtonList;
